import React, { useMemo } from 'react'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import DashboardStackedBarChart from '@components/pages/Dashboard/MetricCharts/DashboardStackedBarChart.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import { DashboardMetricTileGrid } from '@components/pages/Dashboard/Shared/DashboardMetricTileGrid.component'
import type { UseCollectionResult } from '@hooks/dashboard/useCollection'
import type { DsoGroupedBarChartPayload } from '../usePerformanceDashboard'
import { FINANCE_GROSS_NET_BAR_COLORS } from '@constants/Dashboard/financeDashboard.constants'
import { HYGIENE_PLACEHOLDER } from '@utils/dashboardFinance.util'
import {
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'

const DSO_GROUPED_BAR_SERIES = [
  {
    dataKey: 'current',
    name: 'Current DSO',
    color: FINANCE_GROSS_NET_BAR_COLORS.gross,
  },
  {
    dataKey: 'target',
    name: 'Target',
    color: FINANCE_GROSS_NET_BAR_COLORS.net,
  },
] as const

type DsoCardProps = {
  titleHref: string
  collection: UseCollectionResult
  dsoBarChart: DsoGroupedBarChartPayload
}

export function DsoCard({
  titleHref,
  collection,
  dsoBarChart,
}: DsoCardProps): React.ReactElement {
  const {
    loading,
    patientDaysStr,
    insuranceDaysStr,
    patientDaysGoalLabel,
    insuranceDaysGoalLabel,
    hasPayload,
    hasDaysToCollectData,
  } = collection
  // Only days-to-collect drives this card; collection amounts (hasData) and
  // goal targets must not keep it populated.
  const isEmpty = shouldShowDashboardSectionEmpty(loading, hasDaysToCollectData)
  const pending = isDashboardSectionPending(loading, hasPayload)

  const metrics = useMemo((): DashboardMetricItemData[] => {
    const placeholder = HYGIENE_PLACEHOLDER
    return [
      {
        id: 'dso-patient',
        leftLabel: 'Patient',
        value: loading ? placeholder : patientDaysStr,
        rightLabel: patientDaysGoalLabel,
      },
      {
        id: 'dso-insurance',
        leftLabel: 'Insurance',
        value: loading ? placeholder : insuranceDaysStr,
        rightLabel: insuranceDaysGoalLabel,
      },
    ]
  }, [
    loading,
    patientDaysStr,
    insuranceDaysStr,
    patientDaysGoalLabel,
    insuranceDaysGoalLabel,
  ])

  return (
    <div className="dashboard-finance-collection-ratio-column">
      <DashboardSectionCard
        title="DSO (days to collect)"
        titleHref={titleHref}
        className="dashboard-finance-collection-ratio-card dashboard-performance-dso-card"
        loading={pending}
        skeleton={{ metrics: 2, metricColumns: 3, block: 'chart' }}
        isEmpty={isEmpty}
      >
        <div className="dashboard-performance-dso-layout">
          <DashboardMetricTileGrid metrics={metrics} columns={2} />
          {isEmpty ? (
            <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
          ) : (
            <div className="dashboard-performance-dso-chart">
              <DashboardStackedBarChart
                data={loading ? [] : dsoBarChart.data}
                series={[...DSO_GROUPED_BAR_SERIES]}
                horizontal={false}
                stacked={false}
                valueFormatter={(v) => `${v}d`}
                height={290}
                showLegend
                yAxisDomain={dsoBarChart.yDomain}
                yAxisTicks={dsoBarChart.yTicks}
                yAxisWidth={36}
                barSize={90}
                barGap={5}
                barCategoryGap={50}
                margin={{ left: 4, right: 8, top: 8, bottom: 4 }}
                className="dashboard-performance-dso-bar-chart"
              />
            </div>
          )}
        </div>
      </DashboardSectionCard>
    </div>
  )
}
